'use client';

import VideoLazyLoad from '@/app/components/shared/VideoLazyLoad';
import { cn } from '@/app/utils';
import type { SanityMedia } from '../types';
import { encodeHttpMediaUrl } from '../utils/media-url';

interface MediaAssetProps {
  className?: string;
  media?: SanityMedia | null;
}

const VIDEO_EXT_REGEX = /\.(mp4|webm|m4v|mov|ogg|ogv|mpeg|mpg|avi)(\?|#|$)/i;
const IMAGE_EXT_REGEX = /\.(jpe?g|png|gif|webp|avif|svg|bmp|ico)(\?|#|$)/i;

/** 根据 URL 后缀嗅探媒体类型，匹配不到则返回 undefined */
function sniffMediaKind(url?: string | null): 'image' | 'video' | undefined {
  if (!url) return undefined;
  if (VIDEO_EXT_REGEX.test(url)) return 'video';
  if (IMAGE_EXT_REGEX.test(url)) return 'image';
  return undefined;
}

/**
 * 解析 Sanity 媒体字段。
 *
 * 由于资源字段允许选择"外部链接 + 图片/视频"两种来源，运营经常会把视频链接错填进
 * 图片字段（反之亦然），导致 resourceType 与真实 URL 不匹配。这里基于 URL 后缀做
 * 一次嗅探：如果嗅探到的类型与 resourceType 不一致，则以嗅探结果为准，并到对应字段
 * 里把可用 URL 捞出来，避免出现 `<img src="...mp4">` 这种渲染失败的情况。
 */
export function resolveMedia(media?: SanityMedia | null): {
  kind: 'image' | 'video';
  url: string;
  coverUrl?: string;
  alt?: string;
} | null {
  if (!media) return null;

  const imageUrl = media.image?.url ?? null;
  const videoUrl = media.video?.url ?? null;
  const coverUrl = media.video?.coverImage?.url ?? null;
  const alt = media.image?.alt ?? undefined;

  const sniffed = sniffMediaKind(videoUrl) ?? sniffMediaKind(imageUrl);
  const kind = sniffed ?? media.resourceType ?? null;
  if (!kind) return null;

  const url = kind === 'video' ? (videoUrl ?? imageUrl) : (imageUrl ?? videoUrl);
  if (!url) return null;

  return {
    kind,
    url: encodeHttpMediaUrl(url),
    coverUrl: coverUrl ? encodeHttpMediaUrl(coverUrl) : undefined,
    alt,
  };
}

export function MediaAsset({ className, media }: MediaAssetProps) {
  const resolved = resolveMedia(media);

  if (!resolved) {
    return <div className={cn('h-full w-full bg-white/6', className)} />;
  }

  if (resolved.kind === 'image') {
    return (
      <img
        alt={resolved.alt || 'tool image'}
        className={cn('h-full w-full object-cover', className)}
        src={resolved.url}
      />
    );
  }

  return (
    <VideoLazyLoad
      className={cn('h-full w-full object-cover', className)}
      poster={resolved.coverUrl ?? ''}
      videoSrc={resolved.url}
    />
  );
}
